package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.format.LocaleFormat

/**
 * Pure, DOM-free helpers for R-26's "project files" panel in [EditorScreen] -- see
 * `ProjectFileKindClientTest`. Every check here is a CONVENIENCE, never a security boundary: a
 * caller hitting `POST /api/projects/{id}/files` directly bypasses all of it, so the real,
 * load-bearing validation is `dev.kuml.portal.server.files.ProjectFileValidator` server-side. This
 * object exists purely to give the user fast, friendly feedback (and a narrowed file picker)
 * before a network round trip -- mirrors [AiContextFile]'s exact role for R-8's context file.
 */
public object ProjectFileKindClient {
    /** Mirrors the server's default `maxProjectFileBytes` (see
     * `dev.kuml.portal.server.config.PortalServerConfig.maxProjectFileBytes`'s KDoc) -- purely a
     * local pre-check hint, see class KDoc. */
    public const val MAX_BYTES_HINT: Int = 2_097_152

    /** `<input type="file" accept="...">` value -- narrows the OS file picker's default view. */
    public val ACCEPT_ATTRIBUTE: String =
        ".csv,.json,.md,.markdown,.txt,.svg,.png,.jpg,.jpeg,.webp," +
            "text/csv,application/json,text/markdown,text/plain,image/svg+xml,image/png,image/jpeg,image/webp"

    public const val MAX_DESCRIPTION_LENGTH: Int = 300

    /** Derives the wire-value `dev.kuml.portal.server.files.ProjectFileKind.wireName` [fileName]'s
     * extension would resolve to server-side, or `null` if unrecognised. Case-insensitive; only the
     * LAST `.`-separated segment is considered -- mirrors
     * `dev.kuml.portal.server.files.ProjectFileKind.fromFileName`'s exact resolution rule. */
    public fun kindForFileName(fileName: String): String? {
        val dot = fileName.lastIndexOf('.')
        if (dot < 0 || dot == fileName.length - 1) return null
        return when (fileName.substring(dot + 1).lowercase()) {
            "csv" -> "csv"
            "json" -> "json"
            "md", "markdown" -> "markdown"
            "txt" -> "text"
            "svg" -> "svg"
            "png" -> "png"
            "jpg", "jpeg" -> "jpeg"
            "webp" -> "webp"
            else -> null
        }
    }

    /** `true` for the three raster image kinds -- used by the panel to decide whether to render an
     * `<img>` preview or a plain download link. */
    public fun isImage(kind: String): Boolean = kind == "png" || kind == "jpeg" || kind == "webp"

    /** R-18-style size label, delegating the decimal-separator choice to [LocaleFormat.sizeLabel]
     * (decimal/1000-based, matching how a typical OS file browser labels a file). */
    public fun sizeLabel(
        bytes: Int,
        format: LocaleFormat,
    ): String = format.sizeLabel(bytes)
}
